import { RoleDefinitions, RoleSubsets } from './roles';
import { Map } from 'immutable';
import { getProps } from './common';

export class DocumentInfo {
    docType;
    displayName;
    belongsTo;
    limit;
    id;
    typePrefix;

    constructor(type, name, tenants, limit = 1, id = undefined, typePrefix) {
        const all = [RoleDefinitions.Admin.type, RoleDefinitions.Client.type, RoleDefinitions.Contractor.type];
        this.docType = type;
        this.displayName = name;
        this.belongsTo = tenants ? (Array.isArray(tenants) ? tenants : [tenants]) : all;
        this.limit = limit;
        this.id = id ? id : type;
        this.typePrefix = typePrefix;
    }

    static get types() {
        const admin = RoleDefinitions.Admin.type;
        const client = RoleDefinitions.Client.type;
        const contractor = RoleDefinitions.Contractor.type;
        const icSubset = RoleSubsets.ContractorIC.type;
        const w2Subset = RoleSubsets.ContractorW2.type;
        const ic = [contractor, icSubset];
        const w2 = [contractor, w2Subset];
        const icOrW2 = [contractor, icSubset, w2Subset];

        return {
            // Admin specific document types
            ADMIN_MSA: new DocumentInfo('GreenLight-MSA', 'GreenLight Master Service Agreement (MSA)', admin),
            ADMIN_W4: new DocumentInfo('USA-W4', 'Form W-4: Employee’s Withholding Allowance Certificate', admin),
            ADMIN_W9: new DocumentInfo('USA-W9', 'Form W-9: Request for Taxpayer Identification Number and Certification', admin),
            ADMIN_I9: new DocumentInfo('USA-I9', 'Form I-9: Employment Eligibility Verification', admin),
            ADMIN_EMPLOYEE_HANDBOOK: new DocumentInfo('GreenLight-Handbook', 'GreenLight Employee Handbook', admin),
            ADMIN_EMPLOYEE_HANDBOOK_UK: new DocumentInfo('GreenLight-Handbook-UK', 'UK GreenLight Employee Handbook', admin),
            ADMIN_UK_RIGHT_TO_WORK_INSTRUCTIONS: new DocumentInfo('UK-Right-To-Work-Instructions', 'UK Right To Work Instructions', admin),
            ADMIN_CONTRACTOR_AGREEMENT: new DocumentInfo('GreenLight-ICAgreement', 'Independent Service Provider Agreement', admin),
            ADMIN_TIMESHEET_TEMPLATE: new DocumentInfo('Timesheet-Template', 'Timesheet Template', admin),
            ADMIN_CLC_SECTION: new DocumentInfo('CA_LABOR_CODE_SECTIONS', 'California Labor Code Sections 2870-2872', admin),
            ADMIN_CLIENT_INVOICE: new DocumentInfo('CLIENT-INVOICE', 'Client invoice', admin, Number.MAX_SAFE_INTEGER),
            ADMIN_PAY_STUB: new DocumentInfo('PAY-STUB', 'Pay Stub', admin, Number.MAX_SAFE_INTEGER),
            ADMIN_INVENTION_DISCLOSURE: new DocumentInfo('INV_DISC_FORM', 'Invention Disclosures Form', admin),

            // Client specific document types
            CLIENT_NDA: new DocumentInfo('Client-NDA', 'Client NDA', client),
            CLIENT_PIIA: new DocumentInfo('clientPIIA', 'Proprietary Information / Inventions Agreement', client),
            CLIENT_TRAINING: new DocumentInfo('clientOrientationTraining', 'Orientation and Training Manual', client),

            // Contractor specific document types

            //  - applicable to all contractors (W2 and IC)
            CONTRACTOR_CLIENT_DOC: new DocumentInfo('worker-client-doc', 'Client-specific document', icOrW2),
            CONTRACTOR_INVENTION_DISCLOSURE: new DocumentInfo('INV_DISC', 'Invention Disclosures', icOrW2),

            CONTRACTOR_GOOD_STANDING: new DocumentInfo('goodStanding', 'Certificate of Good Standing', ic),
            CONTRACTOR_1099: new DocumentInfo('form1099', '1099 From a Client', ic),
            CONTRACTOR_DBA: new DocumentInfo('dba', 'Fictitious Business Name Statement', ic),
            CONTRACTOR_INCORPORATION: new DocumentInfo('incorporation', 'Certificate of Incorporation', ic),
            CONTRACTOR_MARKETING: new DocumentInfo('marketing', 'Marketing Materials', ic, 6),
            CONTRACTOR_INVESTMENT: new DocumentInfo('investment', 'Proof of Investment', ic),
            CONTRACTOR_INCOME_SOURCE: new DocumentInfo('incomeSources', 'Income Source Doc', ic),
            CONTRACTOR_INSURANCE: new DocumentInfo('insurance', 'Contractor proof of insurance', ic),
            CONTRACTOR_ICAGREEMENT: new DocumentInfo('ic-agreement', 'Independent Service Provider Agreement', ic),
            CONTRACTOR_ASSIGNMENT_IC: new DocumentInfo('assignmentIC', 'Assigned Client Agreement', ic),

            //  - applicable to w2 only
            CONTRACTOR_OFFER_LETTER_OLD: new DocumentInfo('offer-letter', 'Offer Letter', w2, Number.MAX_SAFE_INTEGER),
            CONTRACTOR_OFFER_LETTER: new DocumentInfo('offer-letter2', 'Offer Letter', w2, Number.MAX_SAFE_INTEGER),
            CONTRACTOR_ASSIGNMENT_W2: new DocumentInfo('assignment', 'Assigned Client Agreement', w2),
            CONTRACTOR_RIGHT_TO_WORK: new DocumentInfo('right-to-work', 'Right to work document', w2, 3),
            CONTRACTOR_P45: new DocumentInfo('P45', 'P45', w2, 1),

            //  - I9 related, which is also applicable to w2 only
            CONTRACTOR_USA_I9_SIGNED: new DocumentInfo('USA-I9-SIGNED', 'Signed I9 form', w2),
            //   Type A
            CONTRACTOR_I9_A_US_PASSPORT: new DocumentInfo('US_PASSPORT', 'U.S. Passport photo page', w2, 1, undefined, 'i9-A-'),
            CONTRACTOR_I9_A_US_PASSPORT_CARD: new DocumentInfo('US_PASSPORT_CARD', 'U.S. Passport Card', w2, 1, undefined, 'i9-A-'),
            CONTRACTOR_I9_A_FORM_I551: new DocumentInfo('FORM_I-551', 'Form I-551', w2, 1, undefined, 'i9-A-'),
            CONTRACTOR_I9_A_FPASS_WI551: new DocumentInfo('FPASS_W_I-551', 'Foreign passport with I-551', w2, 1, undefined, 'i9-A-'),
            CONTRACTOR_I9_A_FORM_I766: new DocumentInfo('FORM_I-766', 'Form I-766', w2, 1, undefined, 'i9-A-'),
            CONTRACTOR_I9_A_NONIMMIGRANT_ALIEN: new DocumentInfo('NONIMMIGRANT_ALIEN', 'Foreign passport with I-94 or I-94A', w2, 1, undefined, 'i9-A-'),
            //   Type B
            CONTRACTOR_I9_B_DRIVERS_LICENSE_ID_CARD: new DocumentInfo('DRIVERS_LICENSE_ID_CARD', "Driver's license or ID card", w2, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_STATE_ID: new DocumentInfo('STATE_ID', 'State-issued ID card', w2, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_FEDERAL_ID_CARD: new DocumentInfo('FEDERAL_ID_CARD', 'Government-issued ID card', w2, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_SCHOOL_ID_CARD: new DocumentInfo('SCHOOL_ID_CARD', 'School ID card', w2, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_VOTERS_REGISTRATION_CARD: new DocumentInfo('VOTERS_REGISTRATION_CARD', "Voter's registration card", w2, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_US_MILITARY_CARD: new DocumentInfo('US_MILITARY_CARD', 'U.S. Military document', contractor, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_MILITARY_DEPENDENT_CARD: new DocumentInfo('MILITARY_DEPENDENT_CARD', "Military dependent's ID card", w2, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_COAST_GUARD_CARD: new DocumentInfo('COAST_GUARD_CARD', 'Merchant Mariner Card', contractor, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_NATIVE_AMERICAN_DOC: new DocumentInfo('NATIVE_AMERICAN_DOC', 'Tribal document', contractor, 1, undefined, 'i9-B-'),
            CONTRACTOR_I9_B_CANADIAN_DRIVERS_LICENSE: new DocumentInfo('CANADIAN_DRIVERS_LICENSE', "Canadian driver's license", w2, 1, undefined, 'i9-B-'),
            //   Type C
            CONTRACTOR_I9_C_SSN_CARD: new DocumentInfo('SSN_CARD', 'Social Security card', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_REPORT_OF_BIRTH_FS240: new DocumentInfo('REPORT_OF_BIRTH_FS240', 'FS-240 CRBA', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_REPORT_OF_BIRTH_DS1350: new DocumentInfo('REPORT_OF_BIRTH_DS1350', 'DS-1350', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_REPORT_OF_BIRTH_FS545: new DocumentInfo('REPORT_OF_BIRTH_FS545', 'FS-545', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_BIRTH_CERTIFICATE: new DocumentInfo('BIRTH_CERTIFICATE', 'Birth Certificate', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_NATIVE_AMERICAN_DOC: new DocumentInfo('NATIVE_AMERICAN_DOC', 'Tribal document', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_CITIZEN_CARD_FORM_I197: new DocumentInfo('CITIZEN_CARD_FORM_I-197', 'I-197', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_RESIDENT_CARD_FORM_I197: new DocumentInfo('RESIDENT_CARD_FORM_I-197', 'I-197', w2, 1, undefined, 'i9-C-'),
            CONTRACTOR_I9_C_EMPLOYEMENT_AUTH_HS: new DocumentInfo('EMPLOYEMENT_AUTH_HS', 'Employment authorization document', w2, 1, undefined, 'i9-C-'),

            // General specific document types
            OTHER: new DocumentInfo('Other', 'Other', undefined, 10)
        };
    }

    static get allDocuments() {
        return getProps(DocumentInfo.types);
    }
    static get adminDocuments() {
        return DocumentInfo.getDocsForTenant(RoleDefinitions.Admin.type);
    }
    static get adminListableDocuments() {
        const { ADMIN_CLIENT_INVOICE, ADMIN_PAY_STUB, ADMIN_TIMESHEET_TEMPLATE } = DocumentInfo.types;
        const docs = Map(DocumentInfo.types)
            .filter(
                d =>
                    d.belongsTo.includes(RoleDefinitions.Admin.type) &&
                    ![ADMIN_CLIENT_INVOICE.docType, ADMIN_PAY_STUB.docType, ADMIN_TIMESHEET_TEMPLATE.docType].includes(d.docType)
            )
            .toJS();

        return getProps(docs);
    }
    static get clientDocuments() {
        return DocumentInfo.getDocsForTenant(RoleDefinitions.Client.type);
    }
    static get workerDocuments() {
        return DocumentInfo.getDocsForTenant(RoleDefinitions.Contractor.type);
    }
    static get workerICDocuments() {
        return DocumentInfo.getDocsForTenant(RoleSubsets.ContractorIC.type);
    }

    static getDocsForTenant(tenant) {
        if (!tenant) return [];
        const docs = Map(DocumentInfo.types)
            .filter(d => d.belongsTo.includes(tenant))
            .toJS();
        return getProps(docs);
    }

    static findByType(type) {
        if (!type) return undefined;
        return Map(DocumentInfo.types).find(d => d.docType === type || type === `${d.typePrefix}${d.docType}`);
    }
}

export const PERMISSIONS = {
    // permissions object, to be used when client uploads doc.
    CLIENT_UPLOAD: {
        client: {
            owner_ref_id_field: 'admin_id',
            owner_ref_type: 'admin',
            permission: 'READ'
        },
        position: {
            owner_ref_id_field: 'client_id',
            owner_ref_type: 'client',
            permission: 'READ'
        }
    },

    // default client upload permissions settings.
    DEFAULT: {
        client: {
            owner_ref_id_field: 'admin_id',
            owner_ref_type: 'admin',
            permission: 'READ'
        },
        position: {
            owner_ref_id_field: 'client_id',
            owner_ref_type: 'client',
            permission: 'READ'
        }
    }
};



// WEBPACK FOOTER //
// ./src/utilities/documentTypes.jsx